// マスタCSVエクスポートの選択ポップアップ。
// 13マスタを系統ごとに並べ、選ばれた分だけをファイルにして利用者の端末へ渡す。
// ファイルの中身とファイル名は masterCsvExport が決める。この画面は選択状態と受け渡し操作だけを持つ。
import { useEffect, useState } from "react";
import {
  Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, Stack, Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import type { ExtendedDB } from "../../../types/extendedDB";
import Time from "../../../util/Time";
import { errorMessageOf } from "../../../util/typed";
import { zipOfTextEntries } from "../../../util/zipIo";
import { OfflineMasterBanner } from "../../../components/OfflineMasterBanner";
import type { MasterCsvExportSource, MasterCsvFile } from "./csv/masterCsvExport";
import { exportFileNameOf, masterCsvFilesOf } from "./csv/masterCsvExport";
import type { MasterScreenKey } from "./csv/masterCsvTargets";
import {
  MASTER_CSV_SCREEN_GROUPS, MASTER_EXPORT_BUILD_FAILED_PREFIX, MASTER_EXPORT_BUSY_MESSAGE,
  MASTER_EXPORT_EMPTY_MASTER_DESCRIPTION, MASTER_EXPORT_EMPTY_SELECTION_MESSAGE, MASTER_EXPORT_OFFLINE_MESSAGE,
  MASTER_EXPORT_REFRESH_DESCRIPTION, MASTER_EXPORT_REFRESH_FAILED_PREFIX, MASTER_EXPORT_SELECTION_DESCRIPTION,
  SELECTABLE_SCREEN_COUNT, labelOfScreenKey, selectAllStateOf, toggledAllScreenKeys, toggledScreenKeys,
} from "./csv/masterCsvDialogState";
import { activeRowCountOf } from "./masterRowCounts";

// 配るファイルの実体を作る。1つだけならCSVをそのまま、2つ以上ならまとめた zip にする。
async function downloadableBlobOf(files: readonly MasterCsvFile[]): Promise<Blob> {
  const singleFile = files[0];
  if (files.length === 1 && singleFile !== undefined) {
    return new Blob([singleFile.text], { type: "text/csv;charset=utf-8" });
  }
  return await zipOfTextEntries(files);
}

// できあがったファイルを利用者の端末へ渡す。ブラウザのダウンロード操作そのもののため画面側に置く。
function startDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

// 取り直しの結果。最新のマスタを取れたときだけ書き出しへ進む。
type RefreshOutcome = { ok: true; source: MasterCsvExportSource } | { ok: false; message: string };

// サーバーから取り直したマスタを受け取る。失敗したら、そのまま画面に出せる案内文にして返す。
async function refreshOutcomeOf(refreshMasters: () => Promise<MasterCsvExportSource>): Promise<RefreshOutcome> {
  try {
    const source = await refreshMasters();
    return { ok: true, source };
  } catch (error) {
    return { ok: false, message: `${MASTER_EXPORT_REFRESH_FAILED_PREFIX}${errorMessageOf(error)}` };
  }
}

export type MasterCsvExportDialogProps = {
  open: boolean;
  // 選択欄に出す件数の元。書き出す中身には使わず、押された時点で取り直したものを使う。
  db: ExtendedDB;
  isOnline: boolean;
  // サーバーからマスタを取り直し、その場で読み直した最新の内容を返す。呼び出し側が受け持つ。
  refreshMasters: () => Promise<MasterCsvExportSource>;
  onClose: () => void;
  showError: (message: string) => void;
};

export default function MasterCsvExportDialog({ open, db, isOnline, refreshMasters, onClose, showError }: MasterCsvExportDialogProps) {
  const [selectedKeys, setSelectedKeys] = useState<MasterScreenKey[]>([]);
  const [isBuilding, setIsBuilding] = useState<boolean>(false);
  const [showEmptyNotice, setShowEmptyNotice] = useState<boolean>(false);

  const selectAllState = selectAllStateOf(selectedKeys.length, SELECTABLE_SCREEN_COUNT);
  const canDownload = selectedKeys.length > 0 && !isBuilding && isOnline;

  // ポップアップを開き直したときは、前回の書き出し結果の案内を持ち越さない。
  useEffect(() => {
    if (open) {
      setShowEmptyNotice(false);
    }
  }, [open]);

  const onToggleAll = () => {
    setSelectedKeys(toggledAllScreenKeys(selectedKeys));
    setShowEmptyNotice(false);
  };

  const onToggleScreen = (screenKey: MasterScreenKey) => {
    setSelectedKeys(toggledScreenKeys(selectedKeys, screenKey));
    setShowEmptyNotice(false);
  };

  // 押された時点でサーバーからマスタを取り直し、その内容だけを書き出す。
  // 取り直せなかったときは端末に残っていた控えを配ってしまわないよう、書き出さずに中止する。
  // 取り直せても選んだマスタに書き出せる行が1つも無いときは、異常としてではなく案内としてポップアップ内に留める。
  const onDownload = async () => {
    setIsBuilding(true);
    setShowEmptyNotice(false);
    const refreshed = await refreshOutcomeOf(refreshMasters);
    if (!refreshed.ok) {
      setIsBuilding(false);
      showError(refreshed.message);
      return;
    }
    const outcome = masterCsvFilesOf(refreshed.source, selectedKeys);
    if (outcome.name === "missing") {
      setIsBuilding(false);
      showError(outcome.message);
      return;
    }
    if (outcome.name === "empty") {
      setIsBuilding(false);
      setShowEmptyNotice(true);
      return;
    }
    const fileName = exportFileNameOf(outcome.files, Time.TodayString());
    try {
      const blob = await downloadableBlobOf(outcome.files);
      startDownload(blob, fileName);
      setIsBuilding(false);
      onClose();
    } catch (error) {
      setIsBuilding(false);
      showError(`${MASTER_EXPORT_BUILD_FAILED_PREFIX}${errorMessageOf(error)}`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>CSV エクスポート</DialogTitle>
      <DialogContent dividers>
        <OfflineMasterBanner isOnline={isOnline} message={MASTER_EXPORT_OFFLINE_MESSAGE} />
        {isBuilding && (
          <Stack direction="row" alignItems="center" gap={1.5} py={1}>
            <CircularProgress size={20} />
            <Typography variant="body2">{MASTER_EXPORT_BUSY_MESSAGE}</Typography>
          </Stack>
        )}
        <Typography variant="body2" color="text.secondary">
          {MASTER_EXPORT_SELECTION_DESCRIPTION}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {MASTER_EXPORT_EMPTY_MASTER_DESCRIPTION}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {MASTER_EXPORT_REFRESH_DESCRIPTION}
        </Typography>
        {showEmptyNotice && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            {MASTER_EXPORT_EMPTY_SELECTION_MESSAGE}
          </Alert>
        )}

        <FormControlLabel
          sx={{ mt: 1.5 }}
          control={
            <Checkbox
              inputProps={{ "aria-label": "すべて選択" }}
              checked={selectAllState === "all"}
              indeterminate={selectAllState === "partial"}
              onChange={onToggleAll}
            />
          }
          label={<Typography fontWeight="bold">すべて選択</Typography>}
        />
        <Divider />

        <Stack mt={1} gap={1.5}>
          {MASTER_CSV_SCREEN_GROUPS.map((group) => (
            <Stack key={group.system} gap={0.25}>
              <Typography variant="subtitle2" color="text.secondary">{group.label}</Typography>
              {group.screenKeys.map((screenKey) => (
                <FormControlLabel
                  key={screenKey}
                  sx={{ ml: 1 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedKeys.includes(screenKey)}
                      onChange={() => onToggleScreen(screenKey)}
                    />
                  }
                  label={`${labelOfScreenKey(screenKey)} (${activeRowCountOf(db, screenKey)}件)`}
                />
              ))}
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
        <Button startIcon={<DownloadIcon />} variant="contained" disabled={!canDownload} onClick={onDownload}>
          ダウンロード
        </Button>
      </DialogActions>
    </Dialog>
  );
}
